/**
 * The home page once a release is published — the deploying half (DESIGN §10). It
 * replaces the preparation flow with the deploy dashboard: which components still
 * need something, and the auxiliary steps and the close that finish it. The
 * file-by-file change set lives on the preparing page and in each component's
 * detail; this view only shows that panel while the Components table is not yet
 * up, so the two never compete. Instruction history lives on the host pages.
 *
 * The one thing this view must never do is imply that the checkout it is describing
 * is the release. The system boots from a disk snapshot that can be weeks old, so
 * until a `host.sync` of *this boot* has run the plan on the release document
 * describes files that are not there. That state is the `release_host_starting`
 * phase, and the view says so plainly instead of showing a table it cannot stand
 * behind: no component rows, and the sync is the only action offered.
 *
 * Which makes the sync the one step in this flow that is pure machinery — a single
 * mandatory button with no decision in it, in a flow that otherwise asks the
 * operator something at every stop. It is offered as an action rather than hidden
 * because it is not automated yet; when it is, this branch goes away and the phase
 * becomes as transient as the name suggests.
 */
import { Link as RouterLink } from 'react-router-dom';
import { Alert, Card, CardContent, Chip, Link, Stack, Typography } from '@mui/material';
import {
    INSTRUCTION_KIND,
    RELEASE_ONLY_KINDS,
    compareUrl,
    hostInstance,
    narrationFor,
    type Phase,
} from '@finvari/deployer';
import { componentRows, deployProgress, type DeployProgress } from '../lib/componentRows';
import { pastTagText, shippedRef } from '../lib/releaseSummary';
import type { DeployRelease } from '../lib/homeMode';
import { describeActivity, inflightRows, lastOutcomeToReport, type ActivityHost } from '../lib/instructionActivity';
import { hostView } from '../lib/hostStatus';
import {
    CAPTION_TICK_MS,
    LIVE_TICK_MS,
    useComponents,
    useHost,
    useNow,
    useRecentInstructions,
    useReleaseInstructions,
} from '../hooks';
import { ActionCard } from './ActionCard';
import { ActivityAlert } from './ActivityAlert';
import { AnnouncementPreview } from './AnnouncementPreview';
import { AuxiliarySection } from './AuxiliarySection';
import { ChangeSetView } from './ChangeSetView';
import { CloseReleaseCard } from './CloseReleaseCard';
import { ComponentTable } from './ComponentTable';
import { InfraChangesCard } from './InfraChangesCard';

/** Human phase labels for the header chip. */
const PHASE_LABEL: Partial<Record<Phase, string>> = {
    release_host_starting: 'Waiting for the sync',
    open: 'Deploying',
    closing: 'Closing the release',
};

/**
 * The one-line count beside the table heading. Over the components the release
 * changes, because that is the work it created; a redeploy of an unchanged component
 * is mentioned only when there is one, so the usual case reads short.
 */
function progressText(progress: DeployProgress): string {
    if (progress.changed === 0) return 'This release changes no deployable component.';
    const parts = [`${progress.deployed} of ${progress.changed} deployed`];
    if (progress.acknowledged > 0) parts.push(`${progress.acknowledged} skipped`);
    if (progress.redeployed > 0) parts.push(`${progress.redeployed} redeployed unchanged`);
    return parts.join(' · ');
}

/**
 * The one-line change-set caption: file count, GitHub compare, and the permanent
 * link. Shown on whichever card is answering "what is in this release" — the
 * change-set panel before the table exists, the Components header once it does.
 */
function ShippingCaption({ release }: { release: DeployRelease | undefined }) {
    // The tip is what the host deploys, so it is what the caption and the compare
    // link are about; when it is not the tag, the count says so rather than leaving
    // the operator to notice that a commit they pushed is in this release.
    const pastTag = release && pastTagText(release);
    return (
        <Stack direction="row" spacing={1} alignItems="baseline" sx={{ mb: 1 }} flexWrap="wrap" useFlexGap>
            <Typography variant="body2" color="text.secondary">
                {release?.changes
                    ? `${release.changes.fileCount} files changed between ${release.baselineTag} and ${release.releaseTag}` +
                      (pastTag ? ` — the branch is ${pastTag}, and deploying ships it` : '')
                    : 'The change set is computed during preparation, at the capture step, and carried over by the sync.'}
            </Typography>
            {release && (
                <Link
                    href={compareUrl(release.baselineTag, shippedRef(release))}
                    target="_blank"
                    rel="noreferrer"
                    variant="body2"
                >
                    Compare on GitHub
                </Link>
            )}
            {release && (
                <Link component={RouterLink} to={`/releases/${release.version}/changes`} variant="body2">
                    Permanent link
                </Link>
            )}
        </Stack>
    );
}

export function DeployingView({ version, release, phase }: { version: string; release: DeployRelease | undefined; phase: Phase }) {
    const releaseHost = useHost('release');
    const recent = useRecentInstructions('release').value;
    const inflight = inflightRows(recent);
    // A running compile shows "Running for 4m 10s" and wants the second hand; with
    // nothing in flight the page's only clock-derived text is a coarse "2m ago".
    const now = useNow(inflight.length > 0 ? LIVE_TICK_MS : CAPTION_TICK_MS);
    // A deploy that was rejected or failed leaves no trace in the component rows —
    // the marker simply stays on the old commit — so this is where the operator
    // learns that the last thing they asked for did not happen, and why. Scoped to
    // this release: a failure holds the banner until a newer attempt supersedes it,
    // and the previous release's would otherwise sit at the top of this page for as
    // long as this one is open.
    const lastOutcome = lastOutcomeToReport(
        recent.filter((r) => r.release === version),
        RELEASE_ONLY_KINDS,
        now,
    );
    const activityHost: ActivityHost = {
        role: 'release',
        view: hostView(releaseHost.value, now, releaseHost.loading),
        expectedBootSeconds: hostInstance('release')?.expectedBootSeconds,
    };

    // This release's instructions rather than the host's recent window: the rows key
    // their compiling/deploying/failed state off these, and a release with a few
    // retries in it would otherwise push the earliest components out of the window.
    const releaseInstructions = useReleaseInstructions(version).value;
    const rows = componentRows({
        version,
        plan: release?.plan,
        components: useComponents().value,
        acknowledgements: release?.acknowledgements,
        instructions: releaseInstructions,
    });
    const progress = deployProgress(rows);

    return (
        <Stack spacing={3}>
            <Stack direction="row" spacing={2} alignItems="center">
                <Typography variant="h5">Deploying {version}</Typography>
                <Chip color="primary" label={PHASE_LABEL[phase] ?? phase} />
            </Stack>

            {inflight.length > 0 && (
                <Stack spacing={1}>
                    {inflight.map((row) => (
                        <ActivityAlert
                            key={row.id}
                            host="release"
                            row={row}
                            line={describeActivity(row, activityHost, now)}
                            label={narrationFor(row.kind).title}
                        />
                    ))}
                </Stack>
            )}

            {lastOutcome && (
                <ActivityAlert
                    host="release"
                    row={lastOutcome}
                    line={describeActivity(lastOutcome, activityHost, now)}
                    label={narrationFor(lastOutcome.kind).title}
                />
            )}

            {/*
              * `attention` can be transiently absent right after a sync: the release
              * host writes the change-set pointer to this document before `reconcile`
              * merges in `attention`, so a snapshot can arrive with `release` present
              * but `attention` still undefined. Guard rather than assume the array.
              */}
            {release && (release.attention?.length ?? 0) > 0 && (
                <Stack spacing={1}>
                    {release.attention.map((a) => (
                        <Alert key={a.code} severity="warning">
                            <strong>{a.code}:</strong> {a.message}
                        </Alert>
                    ))}
                </Stack>
            )}

            {phase === 'release_host_starting' && (
                <Stack spacing={2}>
                    <Alert severity="info">
                        {version} is published and tagged. The sync checks out the tag, pulls the DVC files, installs
                        dependencies and works out which components this release changes. Until it has run there is
                        nothing to show here — the system boots from a disk snapshot that predates this release.
                    </Alert>
                    {/*
                      * The phase's one action, in the primary slot: nothing else on this
                      * page is legal until it has run, and it takes no decision from the
                      * operator beyond pressing it.
                      */}
                    <ActionCard
                        host="release"
                        kind={INSTRUCTION_KIND.hostSync}
                        release={version}
                        title={'Sync the system to ' + version}
                        emphasis
                    />
                </Stack>
            )}

            {/*
              * The announcement above the table, read-only: it is the plain-English
              * statement of what this release is, written and approved during
              * preparation, and it is the fastest way for whoever is deploying to check
              * that the table below matches what was promised. Frozen because the
              * review gate has closed — the only thing left to do with it is post it.
              */}
            {release?.announcement?.json && (
                <Card variant="outlined">
                    <CardContent>
                        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
                            <Typography variant="h6">Announcement</Typography>
                            <Chip
                                size="small"
                                color={release.announcement.postedAt ? 'success' : 'default'}
                                variant={release.announcement.postedAt ? 'filled' : 'outlined'}
                                label={release.announcement.postedAt ? 'posted' : 'not posted yet'}
                            />
                        </Stack>
                        <AnnouncementPreview json={release.announcement.json} />
                    </CardContent>
                </Card>
            )}

            {/*
              * The file-by-file change set is the right view until the Components table
              * exists. Once that table is up, each row already lists the files it
              * changes and expanding a row is the actionable version of the same
              * information — a second copy of the full list just competes with it.
              * Instruction history lives on the host pages, not here: the banners
              * above already surface whatever is in flight or just failed.
              */}
            {phase === 'release_host_starting' && (
                <Card variant="outlined">
                    <CardContent>
                        <Typography variant="h6" gutterBottom>
                            What is shipping
                        </Typography>
                        <ShippingCaption release={release} />
                        <ChangeSetView pointer={release?.changes} />
                    </CardContent>
                </Card>
            )}

            {/*
              * Only once this boot has synced. Before that the plan on the document
              * describes a checkout that is not there, and a table of components with
              * Deploy buttons is the one thing this page must not offer on a host that
              * would refuse them all.
              */}
            {phase !== 'release_host_starting' && (
                <Card variant="outlined">
                    <CardContent>
                        <Stack direction="row" spacing={1.5} alignItems="baseline" sx={{ mb: 0.5 }}>
                            <Typography variant="h6">Components</Typography>
                            <Typography variant="body2" color="text.secondary">
                                {progressText(progress)}
                            </Typography>
                        </Stack>
                        <ShippingCaption release={release} />
                        <ComponentTable
                            version={version}
                            rows={rows}
                            instructions={releaseInstructions}
                            changes={release?.changes}
                            phase={phase}
                            now={now}
                            machine={releaseHost.value?.machine}
                        />
                    </CardContent>
                </Card>
            )}

            {/*
              * Also gated on the sync, and for the same reason as the table: announce
              * reads the notes out of the checkout, and the publishes read `docs/` and
              * the factory scripts from it. On an unsynced host they would all be about
              * whatever the disk snapshot holds.
              */}
            {release && phase !== 'release_host_starting' && (
                <>
                    {/*
                      * Directly under the Components table, because it is the answer to
                      * the question that table leaves open. Every row up there ends in a
                      * Deploy button, and none of those deploys applies a Pulumi
                      * program — so an operator who reads the table as "everything this
                      * release needs" has to meet this immediately, not after scrolling
                      * past the announce and publish cards.
                      */}
                    <InfraChangesCard release={release} phase={phase} />
                    <AuxiliarySection release={release} phase={phase} />
                    <CloseReleaseCard release={release} rows={rows} phase={phase} />
                </>
            )}
        </Stack>
    );
}
